import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { players as legacyBasePlayersArr } from "../shared/data/rankings";
import type { Player } from "../shared/models/Player";
import { useSleeperDirectory } from "../shared/hooks/useSleeperDirectory";
import { usePlayers } from "../shared/hooks/usePlayers";
import { importExpertRankingsFile } from "../draftCompanion/utils/expertRankingsImport";
import Headshot from "../shared/components/Rankings/Headshot";
import TeamLogo from "../shared/components/TeamLogo";
import PlayerProfileModal from "../draftCompanion/components/PlayerProfileModal";
import { playerProfilesById } from "../shared/data/playerProfiles";
import { playerGameLogsById } from "../shared/data/playerGameLogs";
import { posColor } from "../shared/utils/posColor";
import { formatTeamAbbreviation } from "../shared/utils/teamAbbreviation";
import { getTierLabel, getTierPalette } from "../shared/tierChart/tierPalette";
import coinStampImage from "../shared/images/coin-stamp.png";
import diamondStampImage from "../shared/images/diamond-stamp.png";
import scamStampImage from "../shared/images/scam-stamp.png";
import trueFantasyReviewImage from "./true-fantasy-review.png";

type RailItem =
  | {
      id: string;
      type: "tier";
      label: string;
      color: string;
      shadowColor: string;
    }
  | {
      id: string;
      type: "player";
      playerId: string;
    };

type VideoViewPanelFrame = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

type VideoViewPanelInteraction =
  | {
      type: "drag";
      pointerId: number;
      startX: number;
      startY: number;
      originX: number;
      originY: number;
    }
  | {
      type: "resize";
      pointerId: number;
      startX: number;
      startY: number;
      originWidth: number;
      originHeight: number;
      originX: number;
      originY: number;
      direction: ResizeDirection;
    };

type PositionFilter = "ALL" | "QB" | "RB" | "WR" | "TE";
type VideoViewStamp = "none" | "scam" | "coin" | "diamond";

function createTierRailItem(index: number): RailItem {
  const palette = getTierPalette(index);
  return {
    id: `rail-tier-${index}-${Math.random().toString(36).slice(2, 8)}`,
    type: "tier",
    label: getTierLabel(index),
    color: palette.color,
    shadowColor: palette.shadowColor,
  };
}

function buildInitialRailItems(playerIds: string[]): RailItem[] {
  return [createTierRailItem(0), ...playerIds.map((playerId) => ({ id: `rail-player-${playerId}`, type: "player" as const, playerId }))];
}

function relabelRailItems(items: RailItem[]): RailItem[] {
  let tierIndex = 0;
  return items.map((item) => {
    if (item.type !== "tier") return item;
    const palette = getTierPalette(tierIndex);
    const next: RailItem = {
      ...item,
      label: getTierLabel(tierIndex),
      color: palette.color,
      shadowColor: palette.shadowColor,
    };
    tierIndex += 1;
    return next;
  });
}

function getPlayerCardStyle(selected: boolean, accent: string, dragging = false): CSSProperties {
  return {
    appearance: "none",
    width: "100%",
    textAlign: "left" as const,
    border: selected ? `1px solid ${accent}` : "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    background: selected
      ? "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.06) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
    color: "#fff",
    padding: "4px 10px",
    display: "grid",
    gridTemplateColumns: "24px 26px minmax(0, 1fr) auto",
    gap: 8,
    alignItems: "center",
    cursor: dragging ? "grabbing" : "grab",
    boxShadow: selected ? "0 18px 28px rgba(0,0,0,0.22)" : "0 10px 18px rgba(0,0,0,0.14)",
    opacity: dragging ? 0.72 : 1,
    touchAction: "none" as const,
  };
}

function SortablePlayerCard(props: {
  player: Player;
  rank: number;
  selected: boolean;
  onSelect: () => void;
  stampType?: VideoViewStamp;
  movementDelta?: number;
}) {
  const { player, rank, selected, onSelect, stampType = "none", movementDelta = 0 } = props;
  const accent = posColor(player.position);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `rail-player-${player.id}`,
    data: {
      type: "player",
    },
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onSelect}
      {...attributes}
      {...listeners}
      style={{
        ...getPlayerCardStyle(selected, accent, isDragging),
        position: "relative",
        transform: CSS.Transform.toString(transform),
        transition,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: 24,
          minWidth: 24,
          height: 24,
          borderRadius: 999,
          background: accent,
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 900,
          boxShadow: "0 10px 18px rgba(0,0,0,0.2)",
        }}
      >
        {rank}
      </div>

      <div
        style={{
          width: 26,
          height: 26,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ transform: "scale(0.56)", transformOrigin: "center" }}>
          <Headshot src={player.imageUrl} alt={player.name} />
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 900, lineHeight: 1, color: "#fff" }}>{player.name}</div>
        <div
          style={{
            marginTop: 1,
            display: "flex",
            alignItems: "center",
            gap: 5,
            color: "rgba(255,255,255,0.74)",
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          <span style={{ color: accent }}>{player.position}</span>
          <span style={{ opacity: 0.44 }}>•</span>
          <TeamLogo team={player.team} size={12} fallback={<span>{formatTeamAbbreviation(player.team, "--")}</span>} />
          <span>{formatTeamAbbreviation(player.team, "FA")}</span>
        </div>
      </div>

      {movementDelta !== 0 ? (
        <div
          aria-label={`${Math.abs(movementDelta)} spots ${movementDelta > 0 ? "up" : "down"} from original rank`}
          style={{
            justifySelf: "end",
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "3px 6px",
            borderRadius: 999,
            background: movementDelta > 0 ? "rgba(34,197,94,0.16)" : "rgba(239,68,68,0.16)",
            color: movementDelta > 0 ? "#4ade80" : "#f87171",
            fontSize: 10,
            fontWeight: 900,
            lineHeight: 1,
            whiteSpace: "nowrap",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
          }}
        >
          <span aria-hidden>{movementDelta > 0 ? "▲" : "▼"}</span>
          <span>{Math.abs(movementDelta)}</span>
        </div>
      ) : null}

      {stampType !== "none" ? (
        <img
          aria-hidden
          className={stampType === "scam" ? "scamStampAnimated" : undefined}
          src={stampType === "coin" ? coinStampImage : stampType === "diamond" ? diamondStampImage : scamStampImage}
          alt=""
          style={{
            position: "absolute",
            right: stampType === "coin" || stampType === "diamond" ? 8 : 8,
            top: stampType === "coin" || stampType === "diamond" ? 6 : "50%",
            transform: stampType === "coin" || stampType === "diamond" ? "none" : "translateY(-50%) rotate(-14deg)",
            width: stampType === "coin" || stampType === "diamond" ? 28 : 64,
            height: stampType === "coin" || stampType === "diamond" ? 28 : undefined,
            objectFit: "contain",
            opacity: stampType === "coin" || stampType === "diamond" ? 1 : 0.95,
            filter: stampType === "coin" || stampType === "diamond"
              ? "drop-shadow(0 4px 8px rgba(0,0,0,0.24))"
              : "drop-shadow(0 4px 8px rgba(0,0,0,0.22))",
            pointerEvents: "none",
            ["--scam-stamp-transform" as string]: "translateY(-50%) rotate(-14deg)",
          }}
        />
      ) : null}
    </button>
  );
}

function SortableTierBar({ item }: { item: Extract<RailItem, { type: "tier" }> }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: {
      type: "tier",
    },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        position: "relative",
        zIndex: isDragging ? 2 : 0,
        height: 22,
        borderRadius: 11,
        background: item.color,
        boxShadow: `inset 0 -1px 0 rgba(0,0,0,0.16), 0 6px 14px ${item.shadowColor}33`,
        overflow: "hidden",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 36%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 30,
          borderRadius: 11,
          background: "rgba(0,0,0,0.16)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 20,
          fontWeight: 900,
          lineHeight: 1,
          textShadow: "0 1px 0 rgba(0,0,0,0.22)",
          boxShadow: "inset -1px 0 0 rgba(255,255,255,0.16)",
        }}
      >
        {item.label}
      </div>
    </div>
  );
}

export default function VideoViewPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const rightPanelRef = useRef<HTMLElement | null>(null);
  const panelInteractionRef = useRef<VideoViewPanelInteraction | null>(null);
  const [rankingIds, setRankingIds] = useState<string[]>([]);
  const [railItems, setRailItems] = useState<RailItem[]>([]);
  const [importedPlayers, setImportedPlayers] = useState<Player[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerStampById, setPlayerStampById] = useState<Record<string, VideoViewStamp>>({});
  const [manuallyMovedPlayerIds, setManuallyMovedPlayerIds] = useState<Set<string>>(() => new Set());
  const [activePositionFilter, setActivePositionFilter] = useState<PositionFilter>("ALL");
  const [importedLabel, setImportedLabel] = useState<string>("");
  const [panelFrame, setPanelFrame] = useState<VideoViewPanelFrame | null>(null);
  const [isCompact, setIsCompact] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 1100
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const { sleeperPlayers, canonicalizePlayers, canonicalizeRankingIds } = useSleeperDirectory({
    legacyPlayers: legacyBasePlayersArr,
  });

  const basePlayers = useMemo(() => {
    const mergedById = new Map<string, Player>();

    for (const source of [canonicalizePlayers(legacyBasePlayersArr), sleeperPlayers, importedPlayers]) {
      for (const player of source) {
        const existing = mergedById.get(player.id);
        mergedById.set(player.id, { ...existing, ...player });
      }
    }

    return Array.from(mergedById.values());
  }, [canonicalizePlayers, importedPlayers, sleeperPlayers]);

  const { playersById } = usePlayers({ basePlayers });

  const selectedPlayer = selectedPlayerId ? playersById[selectedPlayerId] ?? null : null;
  const setStampForSelectedPlayer = useCallback((stamp: VideoViewStamp) => {
    if (!selectedPlayerId) return;
    setPlayerStampById((current) => {
      const next = { ...current };
      next[selectedPlayerId] = current[selectedPlayerId] === stamp ? "none" : stamp;
      return next;
    });
  }, [selectedPlayerId]);

  const triggerImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImport = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer;
          const parsed = importExpertRankingsFile({ fileName: file.name, arrayBuffer: buffer });
          const resolvedPlayers = canonicalizePlayers(parsed.players);
          const importedIds = canonicalizeRankingIds(parsed.rankingIds ?? [], parsed.players);

          if (!importedIds.length) {
            window.alert("No rankings were found in that file.");
            return;
          }

          setImportedPlayers((prev) => {
            const nextById = new Map<string, Player>();
            for (const player of [...prev, ...resolvedPlayers]) {
              nextById.set(player.id, { ...nextById.get(player.id), ...player });
            }
            return Array.from(nextById.values());
          });
          setRankingIds(importedIds);
          setRailItems(buildInitialRailItems(importedIds));
          setPlayerStampById({});
          setManuallyMovedPlayerIds(new Set());
          setActivePositionFilter("ALL");
          setSelectedPlayerId(null);
          setImportedLabel(file.name.replace(/\.[^.]+$/, "").trim() || "Imported Rankings");
        } catch (error: any) {
          window.alert(`Could not import rankings: ${error?.message ?? String(error)}`);
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [canonicalizePlayers, canonicalizeRankingIds]
  );

  const orderedRankingIds = useMemo(() => {
    if (!railItems.length) return rankingIds;
    return railItems.flatMap((item) => (item.type === "player" ? [item.playerId] : []));
  }, [rankingIds, railItems]);

  const rankingPlayers = useMemo(() => {
    return orderedRankingIds
      .map((id) => playersById[id])
      .filter((player): player is Player => Boolean(player));
  }, [orderedRankingIds, playersById]);
  const hasImportedRankings = rankingPlayers.length > 0;
  const profileRankingIds = useMemo(() => {
    if (activePositionFilter === "ALL") return orderedRankingIds;
    return orderedRankingIds.filter((id) => playersById[id]?.position === activePositionFilter);
  }, [activePositionFilter, orderedRankingIds, playersById]);
  const filteredRailItems = useMemo(() => {
    if (activePositionFilter === "ALL") return railItems;
    return railItems.filter((item) => {
      if (item.type === "tier") return true;
      const player = playersById[item.playerId];
      return player?.position === activePositionFilter;
    });
  }, [activePositionFilter, playersById, railItems]);
  const filteredPlayerCount = useMemo(() => {
    return filteredRailItems.filter((item) => item.type === "player").length;
  }, [filteredRailItems]);

  const overallRankingIndexById = useMemo(() => {
    return Object.fromEntries(orderedRankingIds.map((id, index) => [id, index + 1]));
  }, [orderedRankingIds]);
  const displayRankingIndexById = useMemo(() => {
    const rankedIds = activePositionFilter === "ALL"
      ? orderedRankingIds
      : orderedRankingIds.filter((id) => playersById[id]?.position === activePositionFilter);
    return Object.fromEntries(rankedIds.map((id, index) => [id, index + 1]));
  }, [activePositionFilter, orderedRankingIds, playersById]);
  const originalRankingIndexById = useMemo(() => {
    return Object.fromEntries(rankingIds.map((id, index) => [id, index + 1]));
  }, [rankingIds]);
  const rankingMovementById = useMemo(() => {
    return Object.fromEntries(
      orderedRankingIds.map((id, index) => {
        const originalRank = originalRankingIndexById[id];
        const currentRank = index + 1;
        return [id, originalRank ? originalRank - currentRank : 0];
      })
    );
  }, [orderedRankingIds, originalRankingIndexById]);
  const visibleMovementById = useMemo(() => {
    return Object.fromEntries(
      orderedRankingIds.map((id) => [
        id,
        manuallyMovedPlayerIds.has(id) ? rankingMovementById[id] ?? 0 : 0,
      ])
    );
  }, [manuallyMovedPlayerIds, orderedRankingIds, rankingMovementById]);

  useEffect(() => {
    if (!rankingIds.length) {
      setRailItems([]);
      setPlayerStampById({});
      setManuallyMovedPlayerIds(new Set());
      return;
    }

    setRailItems((current) => {
      if (current.length > 0) return current;
      return buildInitialRailItems(rankingIds);
    });
  }, [rankingIds]);

  const addTier = useCallback(() => {
    setRailItems((current) => {
      if (!current.length) {
        return [createTierRailItem(0), createTierRailItem(1)];
      }

      const lastTierIndex = [...current]
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.type === "tier")
        .pop()?.index;

      if (lastTierIndex == null) {
        return [createTierRailItem(0), ...current];
      }

      const firstPlayerAfterTierIndex = current.findIndex(
        (item, index) => index > lastTierIndex && item.type === "player"
      );
      const insertIndex = firstPlayerAfterTierIndex >= 0 ? firstPlayerAfterTierIndex + 1 : lastTierIndex + 1;
      const next = [...current];
      const tierCount = current.filter((item) => item.type === "tier").length;
      next.splice(insertIndex, 0, createTierRailItem(tierCount));
      return relabelRailItems(next);
    });
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeId = String(event.active.id);
      const overId = event.over ? String(event.over.id) : null;

      if (!overId) return;

      setRailItems((current) => {
        const oldIndex = current.findIndex((item) => item.id === activeId);
        const newIndex = current.findIndex((item) => item.id === overId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return current;
        const activeItem = current[oldIndex];
        if (activeItem?.type === "player") {
          setManuallyMovedPlayerIds((existing) => {
            const next = new Set(existing);
            next.add(activeItem.playerId);
            return next;
          });
        }
        return relabelRailItems(arrayMove(current, oldIndex, newIndex));
      });
    },
    []
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const update = () => setIsCompact(window.innerWidth <= 1100);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || isCompact) {
      setPanelFrame(null);
      return undefined;
    }

    const syncPanelFrame = () => {
      const panel = rightPanelRef.current;
      if (!panel) return;

      setPanelFrame((current) => {
        if (current) return current;
        return {
          x: 0,
          y: 0,
          width: panel.offsetWidth,
          height: panel.offsetHeight,
        };
      });
    };

    syncPanelFrame();
    window.addEventListener("resize", syncPanelFrame);
    window.addEventListener("orientationchange", syncPanelFrame);

    return () => {
      window.removeEventListener("resize", syncPanelFrame);
      window.removeEventListener("orientationchange", syncPanelFrame);
    };
  }, [isCompact]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const interaction = panelInteractionRef.current;
      const workspace = workspaceRef.current;
      const panel = rightPanelRef.current;
      if (!interaction || !workspace || !panel || event.pointerId !== interaction.pointerId || isCompact) return;

      const minWidth = 320;
      const minHeight = 280;
      const baseLeft = panel.offsetLeft;
      const baseTop = panel.offsetTop;
      const clampFrame = (next: VideoViewPanelFrame): VideoViewPanelFrame => {
        const width = Math.min(
          Math.max(next.width, minWidth),
          Math.max(minWidth, workspace.clientWidth - baseLeft)
        );
        const height = Math.min(
          Math.max(next.height, minHeight),
          Math.max(minHeight, workspace.clientHeight - baseTop)
        );
        const minX = -baseLeft;
        const maxX = workspace.clientWidth - baseLeft - width;
        const minY = -baseTop;
        const maxY = workspace.clientHeight - baseTop - height;

        return {
          x: Math.min(Math.max(next.x, minX), Math.max(minX, maxX)),
          y: Math.min(Math.max(next.y, minY), Math.max(minY, maxY)),
          width,
          height,
        };
      };

      const deltaX = event.clientX - interaction.startX;
      const deltaY = event.clientY - interaction.startY;

      setPanelFrame((current) => {
        const baseFrame =
          current ?? {
            x: 0,
            y: 0,
            width: panel.offsetWidth,
            height: panel.offsetHeight,
          };

        if (interaction.type === "drag") {
          return clampFrame(
            {
              ...baseFrame,
              x: interaction.originX + deltaX,
              y: interaction.originY + deltaY,
            }
          );
        }

        let nextFrame: VideoViewPanelFrame = {
          x: interaction.originX,
          y: interaction.originY,
          width: interaction.originWidth,
          height: interaction.originHeight,
        };

        if (interaction.direction.includes("e")) {
          nextFrame.width = interaction.originWidth + deltaX;
        }
        if (interaction.direction.includes("s")) {
          nextFrame.height = interaction.originHeight + deltaY;
        }
        if (interaction.direction.includes("w")) {
          nextFrame.width = interaction.originWidth - deltaX;
          nextFrame.x = interaction.originX + deltaX;
        }
        if (interaction.direction.includes("n")) {
          nextFrame.height = interaction.originHeight - deltaY;
          nextFrame.y = interaction.originY + deltaY;
        }

        return clampFrame(nextFrame);
      });
    };

    const clearPointerInteraction = () => {
      panelInteractionRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", clearPointerInteraction);
    window.addEventListener("pointercancel", clearPointerInteraction);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", clearPointerInteraction);
      window.removeEventListener("pointercancel", clearPointerInteraction);
    };
  }, [isCompact]);

  const startDraggingRightPanel = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    if (isCompact || !panelFrame) return;
    event.preventDefault();
    event.stopPropagation();
    panelInteractionRef.current = {
      type: "drag",
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: panelFrame.x,
      originY: panelFrame.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [panelFrame, isCompact]);

  const startResizingRightPanel = useCallback((direction: ResizeDirection) => {
    return (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (isCompact || !panelFrame) return;
      event.preventDefault();
      event.stopPropagation();
      panelInteractionRef.current = {
        type: "resize",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originWidth: panelFrame.width,
        originHeight: panelFrame.height,
        originX: panelFrame.x,
        originY: panelFrame.y,
        direction,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    };
  }, [panelFrame, isCompact]);

  return (
    <div
      className="appViewport"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        paddingTop: 12,
      }}
    >
      {!hasImportedRankings ? (
        <div
          style={{
            borderRadius: 24,
            border: "1px solid rgba(255,255,255,0.12)",
            background:
              "linear-gradient(135deg, rgba(10,14,26,0.96) 0%, rgba(17,27,47,0.92) 45%, rgba(9,63,87,0.88) 100%)",
            boxShadow: "0 22px 44px rgba(0,0,0,0.24)",
            padding: "18px 20px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {importedLabel ? (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.84)",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {importedLabel}
              </div>
            ) : null}

            <button
              type="button"
              onClick={triggerImport}
              style={{
                appearance: "none",
                border: "1px solid rgba(125,211,252,0.4)",
                background: "linear-gradient(135deg, rgba(14,165,233,0.28) 0%, rgba(34,197,94,0.22) 100%)",
                color: "#fff",
                borderRadius: 999,
                padding: "12px 18px",
                fontSize: 14,
                fontWeight: 900,
                letterSpacing: 0.3,
                cursor: "pointer",
                boxShadow: "0 12px 28px rgba(2,132,199,0.16)",
              }}
            >
              Import Rankings
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) handleImport(file);
                event.currentTarget.value = "";
              }}
            />
          </div>
        </div>
      ) : (
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: "none" }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) handleImport(file);
            event.currentTarget.value = "";
          }}
        />
      )}

      <div
        ref={workspaceRef}
        style={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          position: "relative",
          gridTemplateColumns: isCompact ? "1fr" : "minmax(290px, 360px) minmax(0, 1fr)",
          gridTemplateRows: isCompact ? "minmax(280px, 42vh) minmax(0, 1fr)" : undefined,
          gap: 14,
        }}
      >
        <section
          style={{
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 14px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {hasImportedRankings ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {(["ALL", "QB", "RB", "WR", "TE"] as PositionFilter[]).map((filter) => {
                    const active = activePositionFilter === filter;
                    return (
                      <button
                        key={filter}
                        type="button"
                        onClick={() => setActivePositionFilter(filter)}
                        style={{
                          appearance: "none",
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.04)",
                          color: active ? "#0f172a" : "rgba(255,255,255,0.82)",
                          borderRadius: 999,
                          padding: "5px 10px",
                          fontSize: 11,
                          fontWeight: 900,
                          letterSpacing: 0.3,
                          cursor: "pointer",
                          lineHeight: 1,
                        }}
                      >
                        {filter}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={addTier}
                  style={{
                    appearance: "none",
                    border: "none",
                    background: "transparent",
                    color: "rgba(255,255,255,0.82)",
                    padding: 0,
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: 0.3,
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                >
                  Tier +
                </button>
              </>
            ) : null}
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: 14,
              display: "grid",
              gap: 12,
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            className="playerProfileScroll"
          >
            {rankingPlayers.length ? (
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext items={filteredRailItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                  <div style={{ display: "grid", gap: 12 }}>
                    {filteredPlayerCount ? filteredRailItems.map((item) =>
                      item.type === "tier" ? (
                        <SortableTierBar key={item.id} item={item} />
                      ) : (
                        <SortablePlayerCard
                          key={item.id}
                          player={playersById[item.playerId]}
                          rank={displayRankingIndexById[item.playerId] ?? overallRankingIndexById[item.playerId] ?? 0}
                          movementDelta={visibleMovementById[item.playerId] ?? 0}
                          selected={item.playerId === selectedPlayerId}
                          stampType={playerStampById[item.playerId] ?? "none"}
                          onSelect={() => setSelectedPlayerId(item.playerId)}
                        />
                      )
                    ) : (
                      <div
                        style={{
                          minHeight: 180,
                          borderRadius: 20,
                          border: "1px dashed rgba(255,255,255,0.18)",
                          background: "rgba(255,255,255,0.03)",
                          display: "grid",
                          placeItems: "center",
                          textAlign: "center",
                          padding: 24,
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 14,
                          lineHeight: 1.5,
                        }}
                      >
                        No {activePositionFilter} players in this ranking set.
                      </div>
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div
                style={{
                  minHeight: 240,
                  borderRadius: 20,
                  border: "1px dashed rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.03)",
                  display: "grid",
                  placeItems: "center",
                  textAlign: "center",
                  padding: 24,
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 15,
                  lineHeight: 1.5,
                }}
              >
                <div>
                  Import a CSV or Excel rankings file to populate the left-side player cells.
                </div>
              </div>
            )}
          </div>
        </section>

        <section
          ref={rightPanelRef}
          style={{
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: !isCompact && panelFrame ? 2 : 1,
            width: !isCompact && panelFrame ? panelFrame.width : undefined,
            height: !isCompact && panelFrame ? panelFrame.height : undefined,
            transform: !isCompact && panelFrame ? `translate(${panelFrame.x}px, ${panelFrame.y}px)` : undefined,
            transformOrigin: "top left",
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              borderRadius: 24,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.10)",
              background: "rgba(6,10,20,0.84)",
              boxShadow: "0 18px 38px rgba(0,0,0,0.22)",
              position: "relative",
            }}
          >
            {selectedPlayer ? (
              <PlayerProfileModal
                presentation="embedded"
                player={selectedPlayer}
                links={playerProfilesById[selectedPlayer.id] ?? []}
                gameLogs={playerGameLogsById[selectedPlayer.id] ?? []}
                rankingIds={profileRankingIds}
                rankingsRankingIds={profileRankingIds}
                playersById={playersById}
                rankLabel={activePositionFilter === "ALL" ? "Rank" : "Position Rank"}
                onClose={() => setSelectedPlayerId(null)}
              />
            ) : (
              <div
                style={{
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  backgroundImage: `linear-gradient(180deg, rgba(5,8,16,0.12) 0%, rgba(5,8,16,0.42) 100%), url(${trueFantasyReviewImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(4,8,18,0.08) 0%, rgba(4,8,18,0.36) 100%)",
                  }}
                />
              </div>
            )}
          </div>

          {!isCompact ? (
            <>
              <button
                type="button"
                aria-label="Resize panel from top edge"
                onPointerDown={startResizingRightPanel("n")}
                style={{
                  position: "absolute",
                  top: -10,
                  left: 18,
                  right: 18,
                  height: 16,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "ns-resize",
                  zIndex: 5,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from bottom edge"
                onPointerDown={startResizingRightPanel("s")}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 18,
                  right: 18,
                  height: 28,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "ns-resize",
                  zIndex: 5,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from left edge"
                onPointerDown={startResizingRightPanel("w")}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 18,
                  bottom: 18,
                  width: 14,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "ew-resize",
                  zIndex: 5,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from right edge"
                onPointerDown={startResizingRightPanel("e")}
                style={{
                  position: "absolute",
                  right: -10,
                  top: 18,
                  bottom: 18,
                  width: 16,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "ew-resize",
                  zIndex: 5,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from top left corner"
                onPointerDown={startResizingRightPanel("nw")}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: 22,
                  height: 22,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "nwse-resize",
                  zIndex: 6,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from top right corner"
                onPointerDown={startResizingRightPanel("ne")}
                style={{
                  position: "absolute",
                  right: -10,
                  top: -10,
                  width: 26,
                  height: 26,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "nesw-resize",
                  zIndex: 6,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from bottom left corner"
                onPointerDown={startResizingRightPanel("sw")}
                style={{
                  position: "absolute",
                  left: 0,
                  bottom: 0,
                  width: 22,
                  height: 22,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "nesw-resize",
                  zIndex: 6,
                  touchAction: "none",
                }}
              />
              <button
                type="button"
                aria-label="Resize panel from bottom right corner"
                onPointerDown={startResizingRightPanel("se")}
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 22,
                  height: 22,
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  cursor: "nwse-resize",
                  zIndex: 6,
                  touchAction: "none",
                }}
              />
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}
